// src/components/reviews/ReviewsSection.tsx
import React, { useState, useEffect } from 'react';
import { ReviewCard } from './ReviewCard';
import { RatingStars } from './RatingStars';
import { RatingDistribution } from './RatingDistribution';
import { ReviewForm } from './ReviewForm';
import toast from 'react-hot-toast';
import { ProductReview } from "../types";
import { useAuth } from "../contexts/AuthContext";
import { reviewService } from "../services/reviewService";
import { supabase } from "../services/supabase";

interface ReviewsSectionProps {
    productId: number;
    variantId?: number;
    productName: string;
}

type SortOption = 'newest' | 'oldest' | 'highest' | 'lowest';

export const ReviewsSection: React.FC<ReviewsSectionProps> = ({ productId, variantId, productName }) => {
    const { user } = useAuth();
    const [reviews, setReviews] = useState<ProductReview[]>([]);
    const [totalReviews, setTotalReviews] = useState(0);
    const [stats, setStats] = useState<{ average: number; total: number; distribution: any }>({
        average: 0,
        total: 0,
        distribution: { 1: 0, 2: 0, 3: 0, 4: 0, 5: 0 }
    });
    const [loading, setLoading] = useState(true);
    const [loadingStats, setLoadingStats] = useState(true);
    const [page, setPage] = useState(1);
    const [sortBy, setSortBy] = useState<SortOption>('newest');
    const [showForm, setShowForm] = useState(false);
    const [editingReview, setEditingReview] = useState<ProductReview | null>(null);
    const [hasUserReviewed, setHasUserReviewed] = useState(false);
    const [checkingUserReview, setCheckingUserReview] = useState(true);

    const limit = 5;

    useEffect(() => {
        loadAllData();
    }, [variantId, sortBy, page, productId]);

    const loadAllData = async () => {
        // Загружаем все данные параллельно
        await Promise.all([
            loadReviews(),
            loadStats(),
            checkUserReview()
        ]);
    };

    const loadReviews = async () => {
        setLoading(true);
        try {
            let result;
            if (variantId) {
                result = await reviewService.getReviewsByVariant(variantId, page, limit, sortBy);
            } else {
                result = await reviewService.getReviewsByProduct(productId, page, limit, sortBy);
            }
            setReviews(result.reviews);
            setTotalReviews(result.total);
        } catch (error) {
            console.error('Error loading reviews:', error);
        } finally {
            setLoading(false);
        }
    };

    const loadStats = async () => {
        setLoadingStats(true);
        try {
            let statsResult;
            if (variantId) {
                statsResult = await reviewService.getReviewStats(variantId);
            } else {
                statsResult = await reviewService.getProductReviewStats(productId);
            }
            setStats(statsResult);
        } catch (error) {
            console.error('Error loading stats:', error);
        } finally {
            setLoadingStats(false);
        }
    };

    const checkUserReview = async () => {
        setCheckingUserReview(true);
        if (!user) {
            setHasUserReviewed(false);
            setCheckingUserReview(false);
            return;
        }

        try {
            let query = supabase
                .from('product_reviews')
                .select('id')
                .eq('customer_id', user.id)
                .eq('product_id', productId);

            if (variantId) {
                query = query.eq('variant_id', variantId);
            } else {
                query = query.is('variant_id', null);
            }

            const { data } = await query.maybeSingle();
            setHasUserReviewed(!!data);
        } catch (error) {
            console.error('Error checking user review:', error);
            setHasUserReviewed(false);
        } finally {
            setCheckingUserReview(false);
        }
    };

    const handleSubmitReview = async (reviewData: any) => {
        if (!user) throw new Error('Необходимо авторизоваться');

        if (reviewData.reviewId) {
            await reviewService.updateReview(reviewData.reviewId, {
                rating: reviewData.rating,
                title: reviewData.title,
                comment: reviewData.comment,
                advantages: reviewData.advantages,
                disadvantages: reviewData.disadvantages
            });
            toast.success('Отзыв успешно обновлен');
        } else {
            await reviewService.createReview(reviewData, user.id);
            toast.success('Спасибо за ваш отзыв!');
        }

        await loadAllData();
        setShowForm(false);
        setEditingReview(null);
    };

    const handleEdit = (review: ProductReview) => {
        setEditingReview(review);
        setShowForm(true);
    };

    const handleDelete = async () => {
        await loadAllData();
    };

    const loadMore = () => {
        if (reviews.length < totalReviews) {
            setPage(prev => prev + 1);
        }
    };

    const hasMore = page < Math.ceil(totalReviews / limit);

    // Проверяем, идет ли загрузка
    const isInitialLoading = loading && page === 1;
    const isLoadingAny = loading || loadingStats || checkingUserReview;

    // Показываем прелоадер только при начальной загрузке
    if (isInitialLoading) {
        return (
            <div className="reviews-section">
                <div className="reviews-loader">
                    <div className="loader-spinner"></div>
                    <p>Загрузка отзывов...</p>
                </div>
            </div>
        );
    }

    // Определяем, нужно ли показывать кнопку "Написать отзыв"
    const shouldShowWriteButton = user && !hasUserReviewed && !isLoadingAny;

    return (
        <div className="reviews-section">
            <div className="reviews-header">
                <h3>Отзывы покупателей</h3>
                {/* Кнопка показывается только после полной загрузки и если пользователь не оставлял отзыв */}
                {shouldShowWriteButton && stats.total > 0 && (
                    <button className="write-review-btn" onClick={() => setShowForm(true)}>
                        ✍️ Написать отзыв
                    </button>
                )}
            </div>

            {stats.total > 0 ? (
                <div className="reviews-stats">
                    <div className="stats-summary">
                        <div className="average-rating">{stats.average.toFixed(1)}</div>
                        <RatingStars rating={stats.average} size={20} />
                        <div className="stats-total">
                            {stats.total} {stats.total === 1 ? 'отзыв' : stats.total < 5 ? 'отзыва' : 'отзывов'}
                        </div>
                    </div>
                    {/* Можно добавить RatingDistribution если нужно */}
                    {/* <RatingDistribution distribution={stats.distribution} total={stats.total} /> */}
                </div>
            ) : (
                <div className="reviews-empty">
                    <div className="empty-icon">⭐</div>
                    <h4>Пока нет отзывов</h4>
                    <p>Будьте первым, кто оценит этот товар</p>
                    {/* Кнопка для пустого состояния показывается только после загрузки */}
                    {shouldShowWriteButton && (
                        <button className="empty-write-btn" onClick={() => setShowForm(true)}>
                            Написать отзыв
                        </button>
                    )}
                </div>
            )}

            {stats.total > 0 && (
                <div className="reviews-sort-bar">
                    <select
                        className="sort-select"
                        value={sortBy}
                        onChange={(e) => {
                            setSortBy(e.target.value as SortOption);
                            setPage(1);
                        }}
                    >
                        <option value="newest">Сначала новые</option>
                        <option value="oldest">Сначала старые</option>
                        <option value="highest">Сначала высокие оценки</option>
                        <option value="lowest">Сначала низкие оценки</option>
                    </select>
                </div>
            )}

            <div className="reviews-list">
                {reviews.map(review => (
                    <ReviewCard
                        key={review.id}
                        review={review}
                        onHelpfulClick={loadStats}
                        onEdit={handleEdit}
                        onDelete={handleDelete}
                    />
                ))}

                {hasMore && (
                    <div className="load-more-btn">
                        <button onClick={loadMore} disabled={loading}>
                            {loading ? 'Загрузка...' : 'Показать еще'}
                        </button>
                    </div>
                )}
            </div>

            {showForm && (
                <ReviewForm
                    productId={productId}
                    variantId={variantId}
                    review={editingReview}
                    onSubmit={handleSubmitReview}
                    onClose={() => {
                        setShowForm(false);
                        setEditingReview(null);
                    }}
                />
            )}
        </div>
    );
};